import { Box, ButtonBase, Checkbox, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { tokens } from "../../../../theme";
import { useEffect, useMemo, useState } from "react";
import { useGetShippersSelectWithProfilesQuery } from "../../../../newapi/shipper/shipperSlice";
import SHModal from "../../../theme/Modal/SHModal";
import Subheading from "../../../theme/text/Subheading";
import Text from "../../../theme/text/Text";
import { InputDropdown } from "../../../theme/dropdowns/InputDropdown";
import ButtonGroup from "../../../theme/buttons/ButtonGroup";
import Shbutton from "../../../theme/buttons/Shbutton";
import MConfirmation from "../MConfirmation";
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import DeleteIcon from '@mui/icons-material/Delete';

export const MEditBatchOrders = ({ open, handleClose, orders, profileData, groupBy, onSave }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [selectedOrders, setSelectedOrders] = useState([]);
    const [editingOrderId, setEditingOrderId] = useState(null);
    const [localChanges, setLocalChanges] = useState({
        ordersToRemove: [],
        orderChanges: [],
    });

    const [bulkShipperId, setBulkShipperId] = useState(-1);
    const [bulkProfileId, setBulkProfileId] = useState(-1);

    const [editShipperId, setEditShipperId] = useState(-1);
    const [editProfileId, setEditProfileId] = useState(-1);

    const { data: shippersData, isLoading: shippersLoading } = useGetShippersSelectWithProfilesQuery(
        { warehouseId: orders?.[0]?.warehouseId },
        { skip: !open }
    );

    const [confirmDeleteModal, setConfirmDeleteModal] = useState({
        open: false,
        orderIds: [],
    });

    useEffect(() => {
        if (open) {
            setSelectedOrders([]);
            setEditingOrderId(null);
            setLocalChanges({
                ordersToRemove: [],
                orderChanges: [],
            });
            setBulkShipperId(-1);
            setBulkProfileId(-1);
            setEditShipperId(-1);
            setEditProfileId(-1);
        }
    }, [open]);

    const visibleOrders = useMemo(() => {
        return orders.filter(order => !localChanges.ordersToRemove.includes(order.id));
    }, [orders, localChanges.ordersToRemove]);

    const bulkProfiles = useMemo(() => {
        if (bulkShipperId === -1) return [];
        const shipper = shippersData?.find(s => s.id === bulkShipperId);
        return shipper?.shipperProfiles || [];
    }, [bulkShipperId, shippersData]);

    const editProfiles = useMemo(() => {
        if (editShipperId === -1) return [];
        const shipper = shippersData?.find(s => s.id === editShipperId);
        return shipper?.shipperProfiles || [];
    }, [editShipperId, shippersData]);

    const getOrderDisplayData = (order) => {
        const change = localChanges.orderChanges.find(c => c.id === order.id);
        if (change) {
            const shipper = shippersData?.find(s => s.id === change.shipperId);
            const profile = shipper?.shipperProfiles?.find(p => p.id === change.shipperProfileId);
            return {
                shipperId: change.shipperId,
                shipperProfileId: change.shipperProfileId,
                shipperName: shipper?.name || order.shipper?.name,
                profileName: profile?.name || order.shipperProfile?.name,
                isChanged: true,
            };
        }
        return {
            shipperId: order.shipper?.id,
            shipperProfileId: order.shipperProfile?.id,
            shipperName: order.shipper?.name,
            profileName: order.shipperProfile?.name,
            isChanged: false,
        };
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setSelectedOrders(visibleOrders.map(o => o.id));
        } else {
            setSelectedOrders([]);
        }
    };

    const handleSelectOrder = (orderId) => {
        setSelectedOrders(prev => {
            if (prev.includes(orderId)) {
                return prev.filter(id => id !== orderId);
            }
            return [...prev, orderId];
        });
    };

    const handleBulkDelete = () => {
        if (selectedOrders.length === 0) return;
        setConfirmDeleteModal({
            open: true,
            orderIds: selectedOrders,
        });
    };

    const handleSingleDelete = (orderId) => {
        setConfirmDeleteModal({
            open: true,
            orderIds: [orderId],
        });
    };

    const handleConfirmDelete = () => {
        setLocalChanges(prev => ({
            ...prev,
            ordersToRemove: [...prev.ordersToRemove, ...confirmDeleteModal.orderIds],
            orderChanges: prev.orderChanges.filter(c => !confirmDeleteModal.orderIds.includes(c.id)),
        }));
        setSelectedOrders(prev => prev.filter(id => !confirmDeleteModal.orderIds.includes(id)));
        setConfirmDeleteModal({ open: false, orderIds: [] });
    };

    const handleEditOrder = (orderId, order) => {
        if (editingOrderId === orderId) {
            setEditingOrderId(null);
            setEditShipperId(-1);
            setEditProfileId(-1);
        } else {
            const displayData = getOrderDisplayData(order);
            setEditingOrderId(orderId);
            setEditShipperId(displayData.shipperId || -1);
            setEditProfileId(displayData.shipperProfileId || -1);
        }
    };

    const handleApplySingleEdit = (orderId) => {
        if (editShipperId === -1 || editProfileId === -1) return;

        const shipper = shippersData?.find(s => s.id === editShipperId);
        const profile = shipper?.shipperProfiles?.find(p => p.id === editProfileId);

        setLocalChanges(prev => {
            const existingIndex = prev.orderChanges.findIndex(c => c.id === orderId);
            const newChange = {
                id: orderId,
                shipperId: editShipperId,
                shipperProfileId: editProfileId,
                shipper,
                shipperProfile: profile,
            };

            if (existingIndex >= 0) {
                const newChanges = [...prev.orderChanges];
                newChanges[existingIndex] = newChange;
                return { ...prev, orderChanges: newChanges };
            }
            return { ...prev, orderChanges: [...prev.orderChanges, newChange] };
        });
        setEditingOrderId(null);
        setEditShipperId(-1);
        setEditProfileId(-1);
    };

    const handleBulkEdit = () => {
        if (bulkShipperId === -1 || bulkProfileId === -1 || selectedOrders.length === 0) return;

        const shipper = shippersData?.find(s => s.id === bulkShipperId);
        const profile = shipper?.shipperProfiles?.find(p => p.id === bulkProfileId);

        setLocalChanges(prev => {
            const newOrderChanges = [...prev.orderChanges];

            selectedOrders.forEach(orderId => {
                const existingIndex = newOrderChanges.findIndex(c => c.id === orderId);
                const newChange = {
                    id: orderId,
                    shipperId: bulkShipperId,
                    shipperProfileId: bulkProfileId,
                    shipper,
                    shipperProfile: profile,
                };

                if (existingIndex >= 0) {
                    newOrderChanges[existingIndex] = newChange;
                } else {
                    newOrderChanges.push(newChange);
                }
            });

            return { ...prev, orderChanges: newOrderChanges };
        });

        setBulkShipperId(-1);
        setBulkProfileId(-1);
        setSelectedOrders([]);
    };

    const handleSaveChanges = () => {
        onSave(localChanges);
        handleClose();
    };

    const isAllSelected = visibleOrders.length > 0 && selectedOrders.length === visibleOrders.length;
    const isIndeterminate = selectedOrders.length > 0 && selectedOrders.length < visibleOrders.length;

    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }
    return (
        <>
            <SHModal
                open={open}
                onClose={handleClose}
                sx={{
                    body: {
                        maxWidth: 800,
                        maxHeight: "80vh",
                        display: "flex",
                        flexDirection: "column",
                    }
                }}
            >
                <Box
                    sx={{
                        p: 3,
                        borderBottom: `1px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
                    }}
                >
                    <Subheading textAlign="center">{t("edit_orders_in_batch")}</Subheading>
                    {selectedOrders.length > 0 && (
                        <Box
                            sx={{
                                mt:1.5,
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                pl: 3,
                                py: 1,
                                pr:1,
                                // borderBottom: `1px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
                                background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[200],
                                borderRadius:7,
                                
                            }}
                        >
                            <Text noBreak bold>{selectedOrders.length} {t("selected")}</Text>
                            <Box sx={{ flex: 1, display: "flex", gap: 1.5, alignItems: "center" }}>
                                <Box sx={{ width: 160 }}>
                                    <InputDropdown
                                        displayName="name"
                                        valueKey="id"
                                        noSelection={t("shipper")}
                                        closeOnSelection
                                        options={shippersData?.filter(s => s.type !== 'bol') || []}
                                        selected={bulkShipperId}
                                        onChange={(selected) => {
                                            setBulkShipperId(selected);
                                            setBulkProfileId(-1);
                                        }}
                                    />
                                </Box>
                                <Box sx={{ width: 160 }}>
                                    <InputDropdown
                                        displayName="name"
                                        valueKey="id"
                                        noSelection={t("profile")}
                                        closeOnSelection
                                        options={bulkProfiles}
                                        selected={bulkProfileId}
                                        onChange={(selected) => setBulkProfileId(selected)}
                                        disabled={bulkShipperId === -1}
                                    />
                                </Box>
                                <ButtonBase
                                    onClick={handleBulkEdit}
                                    disabled={bulkShipperId === -1 || bulkProfileId === -1}
                                    sx={{
                                        px: 2,
                                        height:32,
                                        display:'flex',
                                        alignItems:'center',
                                        borderRadius: 4,
                                        background: theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[400],
                                        color: colors.txt["light"],
                                        fontFamily: "Roboto, sans-serif",
                                        fontSize: 14,
                                        fontWeight: 500,
                                        opacity: bulkShipperId === -1 || bulkProfileId === -1 ? 0.5 : 1,
                                    }}
                                >
                                    {t("apply")}
                                </ButtonBase>
                            </Box>
                            <ButtonBase
                                onClick={handleBulkDelete}
                                sx={{
                                    px: 2,
                                    height:32,
                                    display:'flex',
                                    alignItems:'center',
                                    borderRadius: 4,
                                    background: colors.red[100],
                                    color: colors.red[400],
                                    border:`1px solid ${colors.red[400]}`,
                                    fontFamily: "Roboto, sans-serif",
                                    fontSize: 14,
                                    fontWeight: 500,
                                    whiteSpace:"nowrap"
                                }}
                            >
                                {t("remove_from_batch")}
                            </ButtonBase>
                        </Box>
                    )}
                </Box>

                

                <Box
                    sx={{
                        background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                        // padding:3,
                        borderTopLeftRadius:16,
                        borderTopRightRadius:16,
                        display:"flex",
                        flexDirection:"column",
                        gap:2
                    }}
                >

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            px: 2,
                            py: 1,
                            borderBottom: `1px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
                            // background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[50],
                            borderTopLeftRadius:16,
                            borderTopRightRadius:16,
                        }}
                    >
                        <Checkbox
                            checked={isAllSelected}
                            indeterminate={isIndeterminate}
                            onChange={handleSelectAll}
                            sx={{
                                color: colors.txt["secondary"],
                                '&.Mui-checked': {
                                    color: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
                                },
                            }}
                        />
                        <Box sx={{ flex: 1, ml: 1,width:120, }}>
                            <Text bold>{t("order")}</Text>
                        </Box>
                        <Box sx={{ width: 200, ml: 1.5 }}>
                            <Text bold>{t("shipper")}</Text>
                        </Box>
                        <Box sx={{ width: 200, ml: 1.5 }}>
                            <Text bold>{t("shipper_profile")}</Text>
                        </Box>
                        <Box sx={{ width: 200, ml: 1.5 }} />
                    </Box>

                    <Box
                        sx={{
                            flex: 1,
                            overflow: "auto",
                            minHeight: 200,
                            maxHeight: 700,
                        }}
                    >
                        {visibleOrders.map((order) => {
                            const displayData = getOrderDisplayData(order);
                            const isEditing = editingOrderId === order.id;
                            const isSelected = selectedOrders.includes(order.id);

                            return (
                                <Box
                                    key={order.id}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        px: 2,
                                        py: 1,
                                        borderBottom: `1px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
                                        background: isSelected
                                            ? (theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[50])
                                            : "transparent",
                                        "&:hover": {
                                            background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[50],
                                        }
                                    }}
                                >
                                    <Checkbox
                                        checked={isSelected}
                                        onChange={() => handleSelectOrder(order.id)}
                                        sx={{
                                            color: colors.txt["secondary"],
                                            '&.Mui-checked': {
                                                color: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
                                            },
                                        }}
                                    />
                                    <Box sx={{ flex: 1, ml: 1,width:120,display:"flex",flexDirection:'column' }}>
                                        <Text semibold noBreak>
                                            {order.webshopreference || order.id}
                                        </Text>
                                        {displayData.isChanged && (
                                            <Text light sx={{ fontSize: 11, color: colors.orange[400] }}>
                                                {t("modified")}
                                            </Text>
                                        )}
                                    </Box>

                                    {isEditing ? (
                                        <>
                                            <Box sx={{ width: 200, ml: 1.5 }}>
                                                <InputDropdown
                                                    displayName="name"
                                                    valueKey="id"
                                                    noSelection={t("shipper")}
                                                    options={shippersData?.filter(s => s.type !== 'bol') || []}
                                                    selected={editShipperId}
                                                    closeOnSelection
                                                    onChange={(selected) => {
                                                        setEditShipperId(selected);
                                                        setEditProfileId(-1);
                                                    }}
                                                    height={28}
                                                />
                                            </Box>
                                            <Box sx={{ width: 200, ml: 1.5 }}>
                                                <InputDropdown
                                                    displayName="name"
                                                    valueKey="id"
                                                    noSelection={t("profile")}
                                                    closeOnSelection
                                                    options={editProfiles}
                                                    selected={editProfileId}
                                                    onChange={(selected) => setEditProfileId(selected)}
                                                    disabled={editShipperId === -1}
                                                    height={28}
                                                />
                                            </Box>
                                        </>
                                    ) : (
                                        <>
                                            <Box sx={{ width: 200, ml: 1.5 }}>
                                                <Text bold>{displayData.shipperName}</Text>
                                            </Box>
                                            <Box sx={{ width: 200, ml: 1.5 }}>
                                                <Text>
                                                    {displayData.profileName || t("unknown_profile")}
                                                </Text>
                                            </Box>
                                        </>
                                    )}

                                    <Box
                                        sx={{
                                            width:200,
                                            display: "flex",
                                            justifyContent: "flex-end",
                                            gap: 1,
                                            ml: 1.5
                                        }}
                                    >
                                        {isEditing ? (
                                            <>
                                                <ButtonBase
                                                    onClick={() => handleApplySingleEdit(order.id)}
                                                    disabled={editShipperId === -1 || editProfileId === -1}
                                                    sx={{
                                                        px: 2,
                                                        py: 0.5,
                                                        borderRadius: 4,
                                                        height:32,
                                                        background: theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[400],
                                                        color: colors.txt["light"],
                                                        fontFamily: "Roboto, sans-serif",
                                                        fontSize: 14,
                                                        fontWeight: 500,
                                                        opacity: editShipperId === -1 || editProfileId === -1 ? 0.5 : 1,
                                                    }}
                                                >
                                                    {t("apply")}
                                                </ButtonBase>
                                                <ButtonBase
                                                    onClick={() => handleEditOrder(order.id, order)}
                                                    sx={{
                                                        px: 2,
                                                        py: 0.5,
                                                        height:32,
                                                        borderRadius: 4,
                                                        background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                                                        color: colors.txt["primary"],
                                                        fontFamily: "Roboto, sans-serif",
                                                        fontSize: 14,
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    {t("cancel")}
                                                </ButtonBase>
                                            </>
                                        ) : (
                                            <>
                                                <ButtonBase
                                                    onClick={() => handleEditOrder(order.id, order)}
                                                    sx={{
                                                        px: 2,
                                                        py: 0.5,
                                                        borderRadius: 4,
                                                        background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                                                        color: colors.txt["primary"],
                                                        fontFamily: "Roboto, sans-serif",
                                                        fontSize: 12,
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    {t("edit")}
                                                </ButtonBase>
                                                <ButtonBase
                                                    onClick={() => handleSingleDelete(order.id)}
                                                    sx={{
                                                        px: 1,
                                                        py: 0.5,
                                                        borderRadius: 4,
                                                        background: colors.red[100],
                                                        color: colors.red[400],
                                                        border: `1px solid ${colors.red[400]}`,
                                                        fontFamily: "Roboto, sans-serif",
                                                        fontSize: 12,
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </ButtonBase>
                                            </>
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}

                        {visibleOrders.length === 0 && (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    py: 4,
                                }}
                            >
                                <Text light>{t("no_orders_remaining")}</Text>
                            </Box>
                        )}
                    </Box>
                </Box>

                <Box
                    component="footer"
                    sx={{
                        width:1,
                        paddingBottom: isStandAlone ? "32px" : "16px",
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        paddingRight:3,
                        zIndex:1058,
                        position:"relative",
                        paddingTop:2,
                        background:`${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                        borderBottomLeftRadius:24,
                        borderBottomRightRadius:24,
                        "@media screen and (max-width: 47.9375em)" : {
                            paddingRight:0,
                        }
                    }}
                >
                    <Shbutton
                        isCancel={true}
                        className={"always-visible"}
                        onClick={handleClose}
                        color="primary"
                        variant="text"
                        startIcon={<ArrowRightAltIcon />}
                    >
                        {t("close")}
                    </Shbutton>
                    <ButtonGroup version={2}>
                        <Shbutton
                            className={"group-btn"}
                            color={theme.palette.mode === "dark" ? "blue" : "primary"}
                            variant="contained"
                            onClick={handleSaveChanges}
                        >
                            {t("save")}
                        </Shbutton>
                    </ButtonGroup>
                </Box>
            </SHModal>

            <MConfirmation
                open={confirmDeleteModal.open}
                handleClose={() => setConfirmDeleteModal({ open: false, orderIds: [] })}
                title={t("remove_from_batch")}
                // subtitle={t("remove_orders_from_batch_subtitle", {
                //     count: confirmDeleteModal.orderIds.length
                // })}
                confirm={handleConfirmDelete}
            />
        </>
    );
};